import React from 'react';
import { withRouter, Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { LANGUAGES } from '../../_i18n/languages';

const Footer = (props) => {
    const currentPath = props.location?.pathname || '/';
    const pathParts = currentPath.split('/').filter(Boolean);
    const localePath = pathParts[0];
    const lang = localePath && LANGUAGES[localePath] ? localePath : 'en';

    const getLocalizedUrl = (subPath) => {
        if (!subPath || subPath === '/') {
            return lang === 'en' ? '/' : `/${lang}`;
        }
        const cleanSub = subPath.startsWith('/') ? subPath.substring(1) : subPath;
        return lang === 'en' ? `/${cleanSub}` : `/${lang}/${cleanSub}`;
    };

    const currentYear = new Date().getFullYear();

    return (
        <footer style={{
            background: 'linear-gradient(180deg, #090d16 0%, #030712 100%)',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            color: '#94a3b8',
            marginTop: 'auto',
            padding: '52px 0 28px 0',
            fontFamily: 'inherit'
        }}>
            <div className="container">
                <div className="row">
                    {/* Brand & Mission Column */}
                    <div className="col-lg-4 col-md-12 mb-4 mb-lg-0 pr-lg-4">
                        <Link to={getLocalizedUrl('/')} style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                            <img src="/logo32.png" alt="timenowin.net" className="logo" style={{ borderRadius: '6px' }} />
                            <span style={{ fontSize: '1.45rem', fontWeight: 800, letterSpacing: '-0.02em', color: '#f8fafc' }}>
                                TimeNow<span style={{ color: '#38bdf8' }}>In</span>
                            </span>
                        </Link>
                        <p style={{ fontSize: '0.92rem', color: '#cbd5e1', fontWeight: 500, lineHeight: 1.6, marginBottom: '10px' }}>
                            <FormattedMessage id="footer.tagline" defaultMessage="Precise atomic time and productivity tools worldwide." />
                        </p>
                        <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5, marginBottom: '18px' }}>
                            <FormattedMessage id="footer.desc" defaultMessage="Real-time world clocks, countdown timers, stopwatch with split laps, and time difference calculators across all time zones." />
                        </p>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: 'rgba(15, 23, 42, 0.8)',
                            border: '1px solid rgba(56, 189, 248, 0.2)',
                            borderRadius: '20px',
                            padding: '6px 14px',
                            fontSize: '0.78rem',
                            color: '#38bdf8',
                            fontWeight: 600
                        }}>
                            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', boxShadow: '0 0 8px #22c55e' }}></span>
                            <FormattedMessage id="footer.status.synced" defaultMessage="Atomic Time Servers Synced" />
                        </div>
                    </div>

                    {/* Tools Column */}
                    <div className="col-lg-3 col-md-4 col-6 mb-4">
                        <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f1f5f9', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '18px' }}>
                            <FormattedMessage id="footer.col.tools" defaultMessage="Tools & Clocks" />
                        </h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            <li style={{ marginBottom: '10px' }}>
                                <Link to={getLocalizedUrl('/')} className="footer-nav-link">
                                    <FormattedMessage id="footer.tool.clock" defaultMessage="World Clock & Weather" />
                                </Link>
                            </li>
                            <li style={{ marginBottom: '10px' }}>
                                <Link to={getLocalizedUrl('/countdown-timer')} className="footer-nav-link">
                                    <FormattedMessage id="footer.tool.timer" defaultMessage="Countdown Timer" />
                                </Link>
                            </li>
                            <li style={{ marginBottom: '10px' }}>
                                <Link to={getLocalizedUrl('/stopwatch')} className="footer-nav-link">
                                    <FormattedMessage id="footer.tool.stopwatch" defaultMessage="Online Stopwatch" />
                                </Link>
                            </li>
                            <li style={{ marginBottom: '10px' }}>
                                <Link to={getLocalizedUrl('/time-difference')} className="footer-nav-link">
                                    <FormattedMessage id="footer.tool.diff" defaultMessage="Time Difference Calculator" />
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Resources Column */}
                    <div className="col-lg-3 col-md-4 col-6 mb-4">
                        <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f1f5f9', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '18px' }}>
                            <FormattedMessage id="footer.col.resources" defaultMessage="Resources & News" />
                        </h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            <li style={{ marginBottom: '10px' }}>
                                <a href="https://timenowin.net/blog" className="footer-nav-link">
                                    <FormattedMessage id="footer.resource.blog" defaultMessage="Time Zone Blog" />
                                </a>
                            </li>
                            <li style={{ marginBottom: '10px' }}>
                                <Link to={getLocalizedUrl('/time-difference/new-york-to-london')} className="footer-nav-link">
                                    New York ↔ London
                                </Link>
                            </li>
                            <li style={{ marginBottom: '10px' }}>
                                <Link to={getLocalizedUrl('/time-difference/sao-paulo-to-miami')} className="footer-nav-link">
                                    São Paulo ↔ Miami
                                </Link>
                            </li>
                            <li style={{ marginBottom: '10px' }}>
                                <Link to={getLocalizedUrl('/time-difference/london-to-tokyo')} className="footer-nav-link">
                                    London ↔ Tokyo
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Legal Column */}
                    <div className="col-lg-2 col-md-4 col-12 mb-4">
                        <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f1f5f9', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '18px' }}>
                            <FormattedMessage id="footer.col.legal" defaultMessage="Legal & Privacy" />
                        </h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            <li style={{ marginBottom: '10px' }}>
                                <Link to={getLocalizedUrl('/terms')} className="footer-nav-link">
                                    <FormattedMessage id="footer.terms" defaultMessage="Terms of Use" />
                                </Link>
                            </li>
                            <li style={{ marginBottom: '10px' }}>
                                <Link to={getLocalizedUrl('/privacy')} className="footer-nav-link">
                                    <FormattedMessage id="footer.privacy" defaultMessage="Privacy Policy" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Sub-Footer / Copyright & Disclaimers */}
                <div style={{
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                    paddingTop: '24px',
                    marginTop: '28px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    fontSize: '0.84rem',
                    color: '#64748b'
                }}>
                    <div>
                        timenowin.net © {currentYear}. <FormattedMessage id="footer.rights" defaultMessage="All rights reserved." />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span>UTC / GMT Atomic Reference Time</span>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default withRouter(Footer);